import { isNil } from "./is"

export declare interface JsonArray extends Array<JsonValue> {}

export declare type JsonObject = {
  [Key in string]?: JsonValue
}

export declare type JsonValue = string | number | boolean | JsonObject | JsonArray | null

export function removeEmptyValues<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== "",
    ),
  ) as T
}

export function isObject(item: unknown): item is Record<string, unknown> {
  return Boolean(item && typeof item === "object" && !Array.isArray(item))
}

/**
 *
 * Deep merge two objects, creating a new instance of the target object
 * NOTE: This does not merge arrays. If a same-key array is found, the source array will replace the
 * target array.
 */
export function deepMerge(
  target: JsonValue | undefined,
  source: JsonValue | undefined,
): JsonValue | undefined {
  // 1. Handle nil inputs
  if (isNil(target)) return source
  if (isNil(source)) return target

  // 2. Handle non-object inputs (non-object target gets replaced by source)
  if (!isObject(target) || !isObject(source)) {
    return source
  }

  // At this point, both target and source are guaranteed to be non-null objects.
  const newTarget: Record<string, JsonValue> = { ...target } as Record<string, JsonValue>
  const sourceObject = source as Record<string, JsonValue>

  for (const key in sourceObject) {
    const sourceValue = sourceObject[key]
    const targetValue = newTarget[key]

    if (isObject(sourceValue)) {
      // If the target key is not an object (or doesn't exist/is null), initialize it to an empty object
      if (!isObject(targetValue)) {
        newTarget[key] = {}
      }

      // FIX: Assign the result of the recursive call back to newTarget[key]
      newTarget[key] = deepMerge(newTarget[key], sourceValue) as JsonValue
    } else {
      // For non-object values, overwrite the property.
      newTarget[key] = sourceValue as JsonValue
    }
  }

  return newTarget
}

/**
 * Deep equals two objects, checking for equality at any level of nesting.
 */
export function deepEquals(obj1: JsonValue | undefined, obj2: JsonValue | undefined): boolean {
  // Handle nil cases
  if (isNil(obj1) && isNil(obj2)) return obj1 === obj2
  if (isNil(obj1) || isNil(obj2)) return false

  // Handle primitive values
  if (typeof obj1 !== "object" || obj1 === null) {
    return obj1 === obj2
  }
  if (typeof obj2 !== "object" || obj2 === null) {
    return false
  }

  // Handle arrays
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2)) return false
    if (obj1.length !== obj2.length) return false
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEquals(obj1[i], obj2[i])) {
        return false
      }
    }
    return true
  }
  if (Array.isArray(obj2)) {
    return false
  }

  // At this point, both must be objects (not arrays, not null, not primitives)
  // Handle objects
  if (!isObject(obj1) || !isObject(obj2)) {
    return false
  }

  const obj1Keys = Object.keys(obj1)
  const obj2Keys = Object.keys(obj2)

  // Must have same number of keys
  if (obj1Keys.length !== obj2Keys.length) {
    return false
  }

  // All keys must match and values must be deeply equal
  for (const key of obj1Keys) {
    if (!(key in obj2)) {
      return false
    }
    const val1 = obj1[key] as JsonValue
    const val2 = obj2[key] as JsonValue
    if (!deepEquals(val1, val2)) {
      return false
    }
  }

  return true
}
